import { useEffect, useRef, useState } from 'react';
import {
  Button,
  Drawer,
  Group,
  Loader,
  Menu,
  SegmentedControl,
  Skeleton,
  Text,
  Textarea,
  UnstyledButton,
} from '@mantine/core';
import { Link } from 'react-router-dom';
import type { Gloss, InstantLookup, ItemView, Lesson } from '@nelnela/contracts';
import type { PhraseSelection, SelectionMode } from '../../lib/selection';
import { glyphFor } from '../../lib/marks';
import { provisionalMeaning } from '../../lib/instant';
import { tokenize } from '../../lib/text';
import { IconChevronLeft, IconChevronRight, IconMore } from './icons';

/** What the panel is showing right now — it opens instantly on tap and fills in (F8). */
interface SheetBase {
  selection: string;
  sentence: string;
  lineId: string;
  /** char offsets of the tapped token / phrase in `sentence` — the exact span to lift in the
   *  highlight (for a phrase: `selectionOffsets(phrase, lines)` in `selectionSentence(...)`) */
  sel?: { charStart: number; charEnd: number };
  /** M3 phrase selection (principle 6): set → the span header with ◀ ▶ nudge handles; a single
   *  tapped word leaves it unset/null and the header is unchanged */
  phrase?: PhraseSelection | null;
  /** how the span was formed (→ `support.selection` on the lookup); informational here */
  mode?: SelectionMode;
  /**
   * M10-i i-1: what the database already knew, in one round trip, before the model answered.
   *
   * It rides on `SheetBase` rather than on the `done` variant because it is present in EVERY
   * state: it is what the `loading` state paints instead of a bare skeleton, and it is what
   * survives `done` — the Haiku gloss replaces the provisional MEANING, and the dictionary facts
   * (lemma, part of speech, level, whether a forms panel exists) stay.
   */
  instant?: InstantLookup | null;
}
export type GlossSheetState =
  | ({ status: 'loading' } & SheetBase)
  | ({ status: 'done'; gloss: Gloss } & SheetBase)
  | ({ status: 'error'; message: string } & SheetBase)
  | ({ status: 'unentitled' } & SheetBase);

/**
 * `already` is M6e §3: this (selection, sentence) is in the learner's saved list ALREADY — from a
 * previous session, or from the double-tap that never touched this button. It is not a fourth
 * step of the save flow, it is what the button says before any of them: the action is spent, and
 * `savedCreate` writing a second identical row is what saying otherwise would cost.
 */
export type SavedState = 'idle' | 'saving' | 'saved' | 'error' | 'already';
export type HearingState = 'idle' | 'loading' | 'playing';

export interface GlossSheetProps {
  state: GlossSheetState | null;
  onClose: () => void;
  /** mobile only: the sheet paused the video (D9) — show ▶ Resume until playback resumes */
  resume: { visible: boolean; onResume: () => void } | null;
  /** paid layer (F4/F8): false → Save / Hear it / Note disabled with one calm line. Default true. */
  entitled?: boolean;
  /** ◀ ▶ nudge handles on the phrase header (`nudgeSelection` on the integrator's side) */
  onNudge?: (side: 'start' | 'end', dir: -1 | 1) => void;
  /** Save (F9a) — the integrator calls `savedCreate`; the sheet shows `savedState` */
  onSave?: () => void;
  savedState?: SavedState;
  /** ▶ Hear it (TTS of the sentence) — the sheet shows `hearing` */
  onHear?: () => void;
  hearing?: HearingState;
  /** one calm line under the actions when Hear it failed (403/429/503 → `ttsCalmLine`) */
  actionLine?: string | null;
  /** "Not the right word?" (overflow) → `glossAlternate`; `alternate` marks a re-glossed result */
  onAlternate?: () => void;
  alternate?: boolean;
  /** Note → inline textarea; the text goes to `savedCreate.note` on the integrator's side (M4
   *  moves it onto the item note) */
  onNote?: (text: string) => void;

  // --- M4a: what the tracking engine adds to this surface (F8) ---
  /** the resolved item behind the selection (a chunk tap, or the lexeme once resolved) */
  item?: ItemView | null;
  /** the grammar strip's content — `lessonForItem`; collapsed by default (spec § 5) */
  lesson?: Pick<Lesson, 'id' | 'title' | 'ruleSummary'> | null;
  /** expanding the strip emits `lesson_viewed` with dwell */
  onLessonOpen?: (lessonId: string) => void;
  onLessonClose?: (lessonId: string, dwellMs: number) => void;
  /** long-press on the glyph = `self_report` (the one self-grade in the app, spec § 7) */
  onSelfReport?: (correct: boolean) => void;
  /** overflow "Stop tracking / I already know this" → `itemSuppress` (principle 3) */
  onSuppress?: (suppress: boolean) => void;
  /** a chunk card's "words" segment: drill from the phrase into its tokens */
  onDrillToWords?: () => void;
  /** F9b: "Save as pattern" — the frame behind this selection */
  onSaveAsPattern?: () => void;
}

/** Ported from GlossPanel.jsx and re-skinned per design frames 1c/1e/1d; one surface for words
 *  and phrases. */
export function GlossSheetContent({
  state,
  onClose,
  resume,
  entitled = true,
  onNudge,
  onSave,
  savedState = 'idle',
  onHear,
  hearing = 'idle',
  actionLine = null,
  onAlternate,
  alternate = false,
  onNote,
  item = null,
  lesson = null,
  onLessonOpen,
  onLessonClose,
  onSelfReport,
  onSuppress,
  onDrillToWords,
  onSaveAsPattern,
}: GlossSheetProps) {
  const [morphOpen, setMorphOpen] = useState(false);
  const [grammarOpen, setGrammarOpen] = useState(false);
  const grammarOpenedAt = useRef(0);
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState('');
  // a new selection resets the inline note
  const selKey = state ? `${state.lineId}\u0000${state.selection}` : '';
  useEffect(() => {
    setNoteOpen(false);
    setNote('');
    setGrammarOpen(false);
  }, [selKey]);
  // The dwell is the whole point of `lesson_viewed` (the grader seeds from it), and the normal
  // reading path is "expand, read, move on" — the strip is almost never collapsed by hand. So the
  // event is emitted when the strip GOES AWAY: a new selection, a closed sheet, an unmount.
  const closeRef = useRef<{
    id: string;
    onClose: NonNullable<GlossSheetProps['onLessonClose']>;
  } | null>(null);
  closeRef.current =
    grammarOpen && lesson && onLessonClose ? { id: lesson.id, onClose: onLessonClose } : null;
  useEffect(
    () => () => {
      const c = closeRef.current;
      if (c) c.onClose(c.id, Date.now() - grammarOpenedAt.current);
    },
    [selKey],
  );

  if (!state) {
    return (
      <div style={{ padding: 16, color: 'var(--nn-muted)', fontSize: 13 }}>
        Tap a word in the transcript to see its gloss here.
      </div>
    );
  }
  const g = state.status === 'done' ? state.gloss : null;
  const r = g?.response ?? null;
  const isPhrase = !!state.phrase;
  const wordCount = isPhrase ? tokenize(state.selection).filter((t) => t.word !== '').length : 1;
  // the paid layer: actions disabled with one calm line, never a modal (F4)
  const gated = !entitled || state.status === 'unentitled';

  // M10-i i-1: the dictionary facts. The part of speech comes from the model once it lands and
  // from the cached lemma before that; the LEVEL only ever comes from the catalog, so it is the
  // one chip that appears instantly and never changes when the gloss arrives.
  const instant = state.instant ?? null;
  // Only while the model is still out: once the real meaning is here, showing a provisional one
  // beside it would be two answers to one question.
  const provisional = state.status === 'loading' ? provisionalMeaning(instant) : null;
  const pos = r?.pos ?? instant?.pos ?? null;
  const level = instant?.catalog?.level ?? null;
  // The skeleton chip is only worth showing when there is nothing real to show in its place.
  const chipsPending = state.status === 'loading' && !pos && level === null;
  const hasChips = !!pos || level !== null || (alternate && !!g) || chipsPending;
  const chips = (
    <>
      {pos && <Chip>{pos}</Chip>}
      {level !== null && <Chip title="where this word sits in the curriculum">L{level}</Chip>}
      {alternate && g && <Chip title="re-glossed with an alternate sense">alternate</Chip>}
      {chipsPending && <Skeleton height={16} width={52} radius="sm" />}
    </>
  );

  const overflow = (
    <Menu position="bottom-end" width={200} shadow="md">
      <Menu.Target>
        <UnstyledButton
          aria-label="More"
          style={{
            width: 32,
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--nn-faint)',
          }}
        >
          <IconMore />
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item onClick={onAlternate} disabled={!onAlternate || !g || gated}>
          Not the right word?
        </Menu.Item>
        <Menu.Item onClick={onSaveAsPattern} disabled={!onSaveAsPattern || gated}>
          Save as pattern…
        </Menu.Item>
        {item && onSuppress && (
          <>
            <Menu.Divider />
            <Menu.Item onClick={() => onSuppress(!item.suppressed)}>
              {item.suppressed ? 'Track this again' : 'I already know this'}
            </Menu.Item>
          </>
        )}
      </Menu.Dropdown>
    </Menu>
  );
  const close = (
    <UnstyledButton
      onClick={onClose}
      aria-label="Close"
      style={{ color: 'var(--nn-faint)', width: 32, height: 32, textAlign: 'center' }}
    >
      ✕
    </UnstyledButton>
  );

  return (
    <div style={{ padding: '6px 16px 14px', userSelect: 'text' }} lang="en">
      {isPhrase ? (
        // span header (frame 1d): ◀ ▶ handles flank the phrase — the left pair moves the START,
        // the right pair moves the END, one word each; four ≥40 px targets
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <NudgePair side="start" onNudge={onNudge} />
          <span
            style={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
              textAlign: 'center',
            }}
          >
            <span lang="ka" style={{ fontSize: 16.5, fontWeight: 500, lineHeight: 1.3 }}>
              {state.selection}
            </span>
            <span style={{ fontSize: 10.5, color: 'var(--nn-faint)' }}>
              {wordCount} {wordCount === 1 ? 'word' : 'words'} · nudge to widen
            </span>
          </span>
          <NudgePair side="end" onNudge={onNudge} />
        </div>
      ) : (
        // header: surface form → lemma
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <MemoryGlyph item={item} onSelfReport={gated ? undefined : onSelfReport} />
          <span lang="ka" style={{ fontSize: 21, fontWeight: 500, lineHeight: 1.3 }}>
            {state.selection}
          </span>
          {r && r.lemma && r.lemma !== state.selection && (
            <>
              <span style={{ fontSize: 12, color: 'var(--nn-faint)' }}>→</span>
              <span lang="ka" style={{ fontSize: 14, color: 'var(--nn-muted)' }}>
                {r.lemma}
              </span>
            </>
          )}
          <span style={{ flex: 1 }} />
          {overflow}
          {close}
        </div>
      )}

      {/* row 2: chips + resume — for a phrase the words/phrase segment leads and the overflow/close
          sit at the right, the chips dropping to their own row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 7, minHeight: 24 }}>
        {/* The span header has no room for the glyph beside the ◀ ▶ handles, so for a phrase it
            leads this row instead — a chunk card is exactly where the memory state matters most. */}
        {isPhrase && <MemoryGlyph item={item} onSelfReport={gated ? undefined : onSelfReport} />}
        {isPhrase ? (
          // M4a: live for a pre-identified chunk — "words" drills from the phrase into its tokens
          // (spec § 3). Still inert for a hand-made span: there is nothing to drill into.
          <SegmentedControl
            size="xs"
            readOnly={!onDrillToWords}
            value="phrase"
            onChange={(v) => {
              if (v === 'words') onDrillToWords?.();
            }}
            data={[
              { value: 'words', label: 'words', disabled: !onDrillToWords },
              { value: 'phrase', label: 'phrase' },
            ]}
            aria-label="words or phrase"
          />
        ) : (
          chips
        )}
        <span style={{ flex: 1 }} />
        {resume?.visible && (
          <UnstyledButton
            onClick={resume.onResume}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              padding: '3px 10px',
              borderRadius: 999,
              border: '1px solid var(--nn-hairline)',
              color: 'var(--nn-text)',
              fontSize: 11,
              whiteSpace: 'nowrap',
            }}
          >
            ▶ Resume
          </UnstyledButton>
        )}
        {isPhrase && overflow}
        {isPhrase && close}
      </div>
      {isPhrase && hasChips && (
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, flexWrap: 'wrap' }}
        >
          {r && r.lemma && r.lemma !== state.selection && (
            <span lang="ka" style={{ fontSize: 12.5, color: 'var(--nn-muted)' }}>
              → {r.lemma}
            </span>
          )}
          {chips}
        </div>
      )}

      {state.status === 'loading' &&
        (provisional ? (
          <ProvisionalBlock
            provisional={provisional}
            lemma={instant?.lemma ?? null}
            selection={state.selection}
          />
        ) : (
          <div style={{ marginTop: 11 }}>
            <Skeleton height={18} width="70%" radius="sm" />
            <Skeleton height={12} width="45%" radius="sm" mt={8} />
            <Skeleton height={44} radius="sm" mt={12} />
          </div>
        ))}

      {state.status === 'unentitled' && (
        <div style={{ marginTop: 11 }}>
          <Text fz={14} c="dimmed">
            Glossing is part of the paid layer.
          </Text>
          <Text fz={12.5} mt={4}>
            <Link to="/me" style={{ color: 'var(--nn-link)' }}>
              Your account →
            </Link>
          </Text>
        </div>
      )}

      {state.status === 'error' && (
        <div style={{ marginTop: 11 }}>
          <Text fz={14} c="dimmed">
            {state.message}
          </Text>
        </div>
      )}

      {r && (
        <>
          <div style={{ marginTop: 11, fontSize: 15.5, color: 'var(--nn-text-strong)' }}>
            {r.meaning}
          </div>
          {r.literal && (
            <div
              style={{
                marginTop: 3,
                fontSize: 12.5,
                color: 'var(--nn-muted)',
                fontStyle: 'italic',
              }}
            >
              lit. {r.literal}
            </div>
          )}

          {/* sentence block */}
          <div
            style={{
              marginTop: 11,
              background: 'var(--nn-hairline)',
              borderRadius: 8,
              padding: '9px 11px',
            }}
          >
            <div lang="ka" style={{ fontSize: 14.5, lineHeight: 1.6 }}>
              <Highlight sentence={state.sentence} selection={state.selection} sel={state.sel} />
            </div>
            {r.sentenceTranslation && (
              <div style={{ fontSize: 12.5, color: 'var(--nn-muted)', marginTop: 3 }}>
                {r.sentenceTranslation}
              </div>
            )}
          </div>

          {/* morphology — collapsed by default (compact depth) */}
          {r.morphology && r.morphology.length > 0 && (
            <div style={{ marginTop: 11 }}>
              <UnstyledButton
                onClick={() => setMorphOpen((o) => !o)}
                aria-expanded={morphOpen}
                style={{
                  fontSize: 10,
                  letterSpacing: '.07em',
                  color: 'var(--nn-faint)',
                  textTransform: 'uppercase',
                }}
              >
                {morphOpen ? '▾' : '▸'} Morphology
              </UnstyledButton>
              {morphOpen && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                  {r.morphology.map((m, i) => (
                    <span
                      key={i}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        padding: '4px 9px',
                        borderRadius: 7,
                        border: '1px solid var(--nn-hairline)',
                      }}
                    >
                      <span lang="ka" style={{ fontSize: 13 }}>
                        {m.part}
                      </span>
                      <span style={{ fontSize: 9.5, color: 'var(--nn-faint)' }}>{m.role}</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {r.note && (
            <div style={{ marginTop: 11, fontSize: 12.5, color: 'var(--nn-muted)' }}>{r.note}</div>
          )}

          {/* the grammar strip (F8/F14) — collapsed by default, like Morphology. Expanding it is
              a `lesson_viewed` event with dwell; grammar-point occurrences have no mark of their
              own (D2: the marks are exactly two), this strip is where they surface. */}
          {lesson && (
            <div style={{ marginTop: 11 }}>
              <UnstyledButton
                onClick={() => {
                  const next = !grammarOpen;
                  setGrammarOpen(next);
                  if (next) {
                    grammarOpenedAt.current = Date.now();
                    onLessonOpen?.(lesson.id);
                  } else {
                    onLessonClose?.(lesson.id, Date.now() - grammarOpenedAt.current);
                  }
                }}
                aria-expanded={grammarOpen}
                style={{
                  fontSize: 10,
                  letterSpacing: '.07em',
                  color: 'var(--nn-faint)',
                  textTransform: 'uppercase',
                }}
              >
                {grammarOpen ? '▾' : '▸'} Grammar · {lesson.title}
              </UnstyledButton>
              {grammarOpen && (
                <div
                  style={{
                    marginTop: 6,
                    fontSize: 13,
                    lineHeight: 1.55,
                    color: 'var(--nn-text)',
                    borderLeft: '2px solid var(--nn-hairline)',
                    paddingLeft: 9,
                  }}
                >
                  {lesson.ruleSummary}
                  <div style={{ marginTop: 6 }}>
                    <Link
                      to={`/words/lesson/${lesson.id}`}
                      style={{ fontSize: 11.5, color: 'var(--nn-link)' }}
                    >
                      Read the lesson →
                    </Link>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* actions — Save · Note · ▶ Hear it (F8); disabled with one calm line when unentitled */}
      <Group gap={8} mt={13} wrap="wrap">
        <Button
          size="xs"
          variant="default"
          disabled={gated || !onSave || savedState === 'already'}
          loading={savedState === 'saving'}
          onClick={onSave}
          title={onSave ? undefined : 'Saving arrives with the integration'}
        >
          {savedState === 'already' ? 'Saved ✓' : 'Save'}
        </Button>
        <Button
          size="xs"
          variant="subtle"
          disabled={gated || !onNote}
          onClick={() => setNoteOpen((o) => !o)}
          aria-expanded={noteOpen}
          title={onNote ? undefined : 'Notes arrive with the integration'}
        >
          Note
        </Button>
        <Button
          size="xs"
          variant="subtle"
          disabled={gated || !onHear}
          onClick={onHear}
          leftSection={
            hearing === 'loading' ? (
              <Loader size={10} color="var(--nn-muted)" />
            ) : (
              <span
                aria-hidden
                style={{
                  fontSize: 10,
                  color: hearing === 'playing' ? 'var(--nn-accent)' : undefined,
                }}
              >
                {hearing === 'playing' ? '♪' : '▶'}
              </span>
            )
          }
          title={onHear ? undefined : 'TTS arrives with the integration'}
        >
          {hearing === 'playing' ? 'Playing' : 'Hear it'}
        </Button>
      </Group>
      {gated && state.status !== 'unentitled' && (
        <Text fz={12} c="dimmed" mt={6}>
          Saving and audio are part of the paid layer.
        </Text>
      )}
      {savedState === 'already' && (
        <Text fz={12} c="dimmed" mt={6}>
          Already in your saved words.
        </Text>
      )}
      {savedState === 'saved' && (
        <Text fz={12} c="dimmed" mt={6} role="status">
          Saved ·{' '}
          <UnstyledButton
            onClick={onHear}
            disabled={!onHear}
            aria-label="Hear it"
            style={{ color: 'var(--nn-text)', fontSize: 12 }}
          >
            ▶
          </UnstyledButton>
        </Text>
      )}
      {savedState === 'error' && (
        <Text fz={12} c="dimmed" mt={6} role="status">
          Couldn’t save this — try again in a moment.
        </Text>
      )}
      {actionLine && (
        <Text fz={12} c="dimmed" mt={6} role="status">
          {actionLine}
        </Text>
      )}
      {noteOpen && !gated && (
        <div style={{ marginTop: 8 }}>
          <Textarea
            size="xs"
            autosize
            minRows={2}
            maxRows={5}
            maxLength={500}
            placeholder="A note to yourself — saved with the item"
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
            aria-label="Note"
          />
          <Group gap={8} mt={6}>
            <Button
              size="xs"
              variant="default"
              disabled={!onNote || note.trim() === ''}
              onClick={() => {
                onNote?.(note.trim());
                setNoteOpen(false);
                setNote('');
              }}
            >
              Save note
            </Button>
            <Button size="xs" variant="subtle" onClick={() => setNoteOpen(false)}>
              Cancel
            </Button>
          </Group>
        </div>
      )}
    </div>
  );
}

/** ◀ ▶ for one edge of the span: dir -1 / +1 moves that edge one word left / right. */
function NudgePair({
  side,
  onNudge,
}: {
  side: 'start' | 'end';
  onNudge?: (side: 'start' | 'end', dir: -1 | 1) => void;
}) {
  const label = side === 'start' ? 'start' : 'end';
  return (
    <span style={{ display: 'inline-flex', gap: 2, flexShrink: 0 }}>
      <NudgeButton
        ariaLabel={`Move ${label} left`}
        disabled={!onNudge}
        onClick={() => onNudge?.(side, -1)}
      >
        <IconChevronLeft />
      </NudgeButton>
      <NudgeButton
        ariaLabel={`Move ${label} right`}
        disabled={!onNudge}
        onClick={() => onNudge?.(side, 1)}
      >
        <IconChevronRight />
      </NudgeButton>
    </span>
  );
}

function NudgeButton({
  ariaLabel,
  disabled,
  onClick,
  children,
}: {
  ariaLabel: string;
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <UnstyledButton
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      style={{
        width: 40,
        height: 40,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 8,
        border: '1px solid var(--nn-hairline)',
        color: 'var(--nn-text)',
        opacity: disabled ? 0.4 : 1,
      }}
    >
      {children}
    </UnstyledButton>
  );
}

/**
 * The memory glyph (spec § 7): ○ unmet · ◐ shaky · ● solid, with retrievability on tap and
 * LONG-PRESS = self-report — the one place in the app where the learner grades themselves, and
 * deliberately hard to hit by accident (D3: the app learns from behaviour, not from asking).
 */
function MemoryGlyph({
  item,
  onSelfReport,
}: {
  item: ItemView | null;
  onSelfReport?: (correct: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressed = useRef(false);
  if (!item) return null;
  const memory = item.memory;
  const glyph = glyphFor(memory);
  const axes = Object.entries(memory?.axes ?? {}) as Array<
    [string, NonNullable<NonNullable<typeof memory>['axes']['read']>]
  >;
  const start = () => {
    longPressed.current = false;
    timer.current = setTimeout(() => {
      longPressed.current = true;
      if (navigator.vibrate) navigator.vibrate(8);
      setOpen(true);
    }, 500);
  };
  const cancel = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };
  return (
    <Menu opened={open} onChange={setOpen} position="bottom-start" width={230} shadow="md">
      <Menu.Target>
        <UnstyledButton
          aria-label={`Memory: ${memory?.overall ?? 'unmet'}${item.suppressed ? ', not tracked' : ''}`}
          onPointerDown={onSelfReport ? start : undefined}
          onPointerUp={cancel}
          onPointerLeave={cancel}
          onContextMenu={(e) => e.preventDefault()}
          onClick={() => {
            if (!longPressed.current) setOpen((o) => !o);
          }}
          style={{
            width: 26,
            height: 26,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 14,
            color: item.suppressed ? 'var(--nn-faint)' : 'var(--nn-muted)',
            opacity: item.suppressed ? 0.5 : 1,
            flexShrink: 0,
          }}
        >
          {glyph}
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown>
        <div style={{ padding: '6px 10px 2px' }}>
          {axes.length === 0 ? (
            <Text fz={11.5} c="dimmed">
              Not met yet.
            </Text>
          ) : (
            axes.map(([axis, a]) => (
              <div
                key={axis}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 10,
                  fontSize: 11.5,
                }}
              >
                <span style={{ color: 'var(--nn-muted)' }}>{axis}</span>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {Math.round(a.retrievability * 100)}%{' '}
                  <span style={{ color: 'var(--nn-faint)' }}>
                    {a.stage}
                    {a.provenance !== 'fitted' ? ' ·assumed' : ''}
                  </span>
                </span>
              </div>
            ))
          )}
          {memory && memory.exposures > 0 && (
            <Text fz={10.5} c="dimmed" mt={4}>
              met {memory.exposures}×
            </Text>
          )}
        </div>
        {onSelfReport && (
          <>
            <Menu.Divider />
            <Menu.Item onClick={() => onSelfReport(true)}>I know this one</Menu.Item>
            <Menu.Item onClick={() => onSelfReport(false)}>I don’t know this one</Menu.Item>
          </>
        )}
      </Menu.Dropdown>
    </Menu>
  );
}

/**
 * M10-i i-1: what the app already knew, painted the moment the sheet opens.
 *
 * THE PROMISE THIS MAKES AND KEEPS. It is provisional and it says so — in words, not in a subtle
 * grey. A learner who reads a meaning here and then watches it change has been misled unless the
 * sheet told them first that this was the old answer to a nearly-identical question.
 *
 * The two sources say different things and are labelled differently, because the caveats are
 * different: a PRIOR GLOSS was right about this word in a different sentence (so it may be the
 * wrong sense here), and the DICTIONARY is right about the lemma (so it may be the wrong sense
 * anywhere). Naming the earlier sentence lets the learner judge which — that is the whole reason
 * it is carried on the wire.
 *
 * The skeleton stays underneath: the model is still coming, and this block is not it.
 */
function ProvisionalBlock({
  provisional,
  lemma,
  selection,
}: {
  provisional: NonNullable<ReturnType<typeof provisionalMeaning>>;
  lemma: string | null;
  selection: string;
}) {
  return (
    <div style={{ marginTop: 11 }}>
      <div style={{ fontSize: 15.5, color: 'var(--nn-text-strong)' }}>{provisional.text}</div>
      <div style={{ fontSize: 11.5, color: 'var(--nn-faint)', marginTop: 3 }}>
        {provisional.source === 'gloss' ? (
          <>
            from when you looked{' '}
            {lemma && lemma !== selection ? <span lang="ka">{selection}</span> : 'this'} up before
            {provisional.sentence ? (
              <>
                {' — '}
                <span lang="ka" style={{ fontStyle: 'italic' }}>
                  {provisional.sentence}
                </span>
              </>
            ) : null}
          </>
        ) : (
          'the dictionary meaning — checking this sentence'
        )}
      </div>
      {/* Still loading: the model has not answered, and this block is not its answer. */}
      <Skeleton height={12} width="45%" radius="sm" mt={10} />
      <Skeleton height={44} radius="sm" mt={8} />
    </div>
  );
}

function Chip({ children, title }: { children: React.ReactNode; title?: string }) {
  return (
    <span
      title={title}
      style={{
        fontSize: 10.5,
        padding: '2px 7px',
        borderRadius: 5,
        background: 'var(--nn-panel-2)',
        color: 'var(--nn-muted)',
      }}
    >
      {children}
    </span>
  );
}

/** The sentence with the tapped token / phrase lifted — by its char offsets when known, else the
 *  first occurrence of the selection text. */
function Highlight({
  sentence,
  selection,
  sel,
}: {
  sentence: string;
  selection: string;
  sel?: { charStart: number; charEnd: number };
}) {
  let start = -1;
  let end = -1;
  if (sel && sel.charEnd <= sentence.length && sel.charStart < sel.charEnd) {
    // the token may carry trailing punctuation; lift only the word inside it
    const raw = sentence.slice(sel.charStart, sel.charEnd);
    const inner = raw.indexOf(selection);
    start = sel.charStart + (inner === -1 ? 0 : inner);
    end = inner === -1 ? sel.charEnd : start + selection.length;
  } else if (selection) {
    start = sentence.indexOf(selection);
    end = start + selection.length;
  }
  if (start === -1) return <>{sentence}</>;
  return (
    <>
      {sentence.slice(0, start)}
      <span style={{ fontWeight: 600, color: 'var(--nn-text-strong)' }}>
        {sentence.slice(start, end)}
      </span>
      {sentence.slice(end)}
    </>
  );
}

/**
 * Mobile: Mantine Drawer. Portrait: from the bottom, its max height stopping at the player's
 * bottom edge — it never covers the iframe (F4/F8) — so panel and overlay both start at
 * `insetPx` from the top. Landscape phone (two-panel layout): from the right, over the
 * transcript column only, so `insetPx` is the player column's right edge.
 *
 * Portrait: the transcript stays live under the open sheet (M3 phrase selection — "gloss open on
 * A, tap B" extends the span, and a swipe re-glosses), so the dimming overlay only *looks* modal:
 * it lets taps through and does not close the sheet. ✕ / Esc close it. Landscape keeps the plain
 * overlay (the drawer covers the whole transcript column there anyway).
 */
export function GlossDrawer({
  opened,
  side,
  insetPx,
  ...rest
}: GlossSheetProps & { opened: boolean; side: 'bottom' | 'right'; insetPx: number }) {
  const bottom = side === 'bottom';
  const size = bottom ? `calc(100dvh - ${insetPx}px)` : `calc(100vw - ${insetPx}px)`;
  return (
    <Drawer
      opened={opened}
      onClose={rest.onClose}
      position={side}
      size={size}
      withCloseButton={false}
      trapFocus={false}
      lockScroll={false}
      closeOnClickOutside={!bottom}
      overlayProps={{
        backgroundOpacity: 0.42,
        blur: 0,
        style: bottom ? { top: insetPx, pointerEvents: 'none' } : { left: insetPx },
      }}
      styles={{
        content: bottom
          ? {
              maxHeight: size,
              height: 'auto',
              borderTopLeftRadius: 14,
              borderTopRightRadius: 14,
              borderTop: '1px solid var(--nn-hairline)',
              boxShadow: 'var(--mantine-shadow-xl)',
              background: 'var(--nn-panel)',
            }
          : {
              borderLeft: '1px solid var(--nn-hairline)',
              boxShadow: 'var(--mantine-shadow-lg)',
              background: 'var(--nn-panel)',
              overflowY: 'auto',
            },
        body: { padding: 0 },
      }}
      transitionProps={{ duration: 160 }}
    >
      {bottom && (
        <div
          aria-hidden
          style={{
            width: 36,
            height: 4,
            borderRadius: 2,
            background: 'var(--nn-hairline)',
            margin: '8px auto 4px',
          }}
        />
      )}
      <GlossSheetContent {...rest} />
    </Drawer>
  );
}
